import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningIcon from '@mui/icons-material/Warning';
import RuleIcon from '@mui/icons-material/Rule';
import ErrorIcon from '@mui/icons-material/Error';
import type { AISignal } from '../api/aiSearchTypes';
import { Badge } from './shared';
import type { ReactNode } from 'react';

const SENTIMENT_ICONS = {
    bearish: ArrowDownwardIcon,
    bullish: ArrowUpwardIcon,
    neutral: RemoveIcon,
    anomaly: WarningIcon,
    mixed: RuleIcon,
    warning: WarningIcon,
    risk: ErrorIcon,
} as const;

const SENTIMENT_PALETTE = {
    bearish: 'error',
    bullish: 'success',
    neutral: 'warning',
    anomaly: 'warning',
    mixed: 'info',
    warning: 'warning',
    risk: 'error',
} as const;

interface AISignalCardProps {
    signal: AISignal;
}

export function AISignalCard({ signal }: AISignalCardProps) {
    const theme = useTheme();
    const Icon = SENTIMENT_ICONS[signal.sentiment] || null;
    const paletteKey = SENTIMENT_PALETTE[signal.sentiment] || 'primary';
    const color = theme.palette[paletteKey].main;

    return (
        <Box
            sx={{
                py: 1.25,
                px: 1,
                mx: -1,
                borderBottom: 1,
                borderColor: 'divider',
                borderRadius: '6px',
            }}
        >
            {/* Title row: icon + title */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 0.5,
                }}
            >
                <Icon sx={{ fontSize: 14, color }} />
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {signal.title}
                </Typography>
            </Box>

            {/* Description */}
            <Typography
                sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    lineHeight: 1.35,
                    mb: 0.75,
                }}
            >
                {parseMarkdown(signal.description)}
            </Typography>

            {/* Sentiment badge + inferred label */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Badge color={color}>{signal.sentiment}</Badge>
                    {signal.inferred && (
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '10px',
                                fontStyle: 'italic',
                                color: 'text.disabled',
                            }}
                        >
                            inferred
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

function parseMarkdown(text: string): ReactNode[] {
    const parts: ReactNode[] = [];
    const re = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        if (match[2] !== undefined) {
            parts.push(<strong key={key++}>{match[2]}</strong>);
        } else if (match[3] !== undefined) {
            parts.push(<em key={key++}>{match[3]}</em>);
        }
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
}
